import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  Bot,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  CircleCheckBig,
  Copy,
  Download,
  Loader2,
  RefreshCw,
  User,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import {
  isErrorEventSeverity,
  isErrorEventStatus,
  SeverityBadge,
  SEVERITY_SELECT_OPTIONS,
  StatusBadge,
  STATUS_SELECT_OPTIONS,
} from './badges';
import { formatDateTime, shortenUuid } from './format';
import { ErrorEventDetailDialog } from './ErrorEventDetailDialog';
import { apiService, ErrorEvent, ErrorEventFilter } from '../../../services/api';
import { copyToClipboard } from '../../../utils/clipboard';
import { extractErrorMessage } from '../../../utils/errorHandler';

const SERVICE_OPTIONS = [
  'api-gateway',
  'agent-management',
  'chat-engine',
  'llm-service',
  'widget-service',
  'error-processor',
  'chat-worker',
  'livekit-bot',
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000];

const EMPTY_FILTER: ErrorEventFilter = {};

const FILTER_CHIP_LABELS: Record<keyof ErrorEventFilter, string> = {
  service: 'Service',
  errorIdentifier: 'Fehler-Identifier',
  customerId: 'Kunde',
  severity: 'Severity',
  status: 'Status',
  from: 'Von',
  to: 'Bis',
};

function getActiveFilterChips(filter: ErrorEventFilter): { key: string; label: string; value: string }[] {
  return (Object.keys(filter) as (keyof ErrorEventFilter)[])
    .filter((key) => filter[key] !== undefined && filter[key] !== '')
    .map((key) => ({
      key,
      label: FILTER_CHIP_LABELS[key],
      value: key === 'from' || key === 'to' ? formatDateTime(filter[key]) : String(filter[key]),
    }));
}

export interface ErrorEventsTabProps {
  /** Wird bei jedem Test-Event-Erfolg hochgezählt, damit die Liste sich neu lädt. */
  refreshSignal: number;
}

/** Tab „Error Events" — Filter, Tabelle, Paginierung, Detail-Modal, Bulk-Aktionen. */
export function ErrorEventsTab({ refreshSignal }: ErrorEventsTabProps) {
  const [events, setEvents] = useState<ErrorEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Formular-Entwurf — wirkt erst nach „Anwenden" auf appliedFilter.
  const [draftService, setDraftService] = useState('all');
  const [draftErrorIdentifier, setDraftErrorIdentifier] = useState('');
  const [draftCustomerId, setDraftCustomerId] = useState('');
  const [draftSeverity, setDraftSeverity] = useState('all');
  const [draftStatus, setDraftStatus] = useState('all');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');

  const [appliedFilter, setAppliedFilter] = useState<ErrorEventFilter>(EMPTY_FILTER);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const [selectedEvent, setSelectedEvent] = useState<ErrorEvent | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionPending, setActionPending] = useState(false);

  const [bulkPending, setBulkPending] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Checkbox-Auswahl — bleibt beim Blättern erhalten, wird bei Filterwechsel geleert.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getErrorEvents({ ...appliedFilter, page, limit });
      setEvents(response.items);
      setTotal(response.total);
    } catch (err) {
      setError('Fehler-Events konnten nicht geladen werden');
      toast.error(extractErrorMessage(err, 'Fehler-Events konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }, [appliedFilter, page, limit, refreshSignal]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleApplyFilters = () => {
    setAppliedFilter({
      service: draftService !== 'all' ? draftService : undefined,
      errorIdentifier: draftErrorIdentifier.trim() || undefined,
      customerId: draftCustomerId.trim() || undefined,
      severity: draftSeverity !== 'all' && isErrorEventSeverity(draftSeverity) ? draftSeverity : undefined,
      status: draftStatus !== 'all' && isErrorEventStatus(draftStatus) ? draftStatus : undefined,
      from: draftFrom ? new Date(draftFrom).toISOString() : undefined,
      to: draftTo ? new Date(draftTo).toISOString() : undefined,
    });
    setPage(1);
    setSelectedIds(new Set());
  };

  const handleResetFilters = () => {
    setDraftService('all');
    setDraftErrorIdentifier('');
    setDraftCustomerId('');
    setDraftSeverity('all');
    setDraftStatus('all');
    setDraftFrom('');
    setDraftTo('');
    setAppliedFilter(EMPTY_FILTER);
    setPage(1);
    setSelectedIds(new Set());
  };

  // Exportiert den angewendeten Filter (nicht den Formular-Entwurf) — alle Seiten, nicht nur die angezeigte.
  const handleExport = async () => {
    const { from, to } = appliedFilter;
    if (!from || !to) return;
    // Anzahl beim Start festhalten — ein Filterwechsel während des Downloads ändert `total`.
    const exportedTotal = total;
    setExporting(true);
    try {
      const blob = await apiService.exportErrorEvents(appliedFilter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `error-events-${from.slice(0, 10)}_${to.slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${exportedTotal} Events als CSV exportiert`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Export fehlgeschlagen'));
    } finally {
      setExporting(false);
    }
  };

  const toggleEventSelection = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const togglePageSelection = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const event of events) {
        if (checked) next.add(event.id);
        else next.delete(event.id);
      }
      return next;
    });
  };

  const openDetail = (event: ErrorEvent) => {
    setSelectedEvent(event);
    setDetailOpen(true);
  };

  const replaceEventInList = (updated: ErrorEvent) => {
    setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  };

  const handleAcknowledge = async (event: ErrorEvent) => {
    try {
      setActionPending(true);
      const updated = await apiService.acknowledgeErrorEvent(event.id);
      setSelectedEvent(updated);
      replaceEventInList(updated);
      toast.success('Event quittiert');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Quittieren fehlgeschlagen'));
    } finally {
      setActionPending(false);
    }
  };

  const handleResolve = async (event: ErrorEvent) => {
    try {
      setActionPending(true);
      const updated = await apiService.resolveErrorEvent(event.id);
      setSelectedEvent(updated);
      replaceEventInList(updated);
      toast.success('Event als gelöst markiert');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Als gelöst markieren fehlgeschlagen'));
    } finally {
      setActionPending(false);
    }
  };

  const handleBulkAcknowledge = async () => {
    try {
      setBulkPending(true);
      const result = await apiService.bulkAcknowledgeErrorEvents(appliedFilter);
      toast.success(`${result.updated} Events quittiert`);
      await loadEvents();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Bulk-Quittieren fehlgeschlagen'));
    } finally {
      setBulkPending(false);
    }
  };

  const handleBulkResolve = async () => {
    try {
      setBulkPending(true);
      const result = await apiService.bulkResolveErrorEvents(appliedFilter);
      toast.success(`${result.updated} Events als gelöst markiert`);
      await loadEvents();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Bulk-Lösen fehlgeschlagen'));
    } finally {
      setBulkPending(false);
    }
  };

  const handleSelectionAcknowledge = async () => {
    try {
      setBulkPending(true);
      const result = await apiService.bulkAcknowledgeErrorEvents({ ids: [...selectedIds] });
      toast.success(`${result.updated} Events quittiert`);
      setSelectedIds(new Set());
      await loadEvents();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Quittieren der Auswahl fehlgeschlagen'));
    } finally {
      setBulkPending(false);
    }
  };

  const handleSelectionResolve = async () => {
    try {
      setBulkPending(true);
      const result = await apiService.bulkResolveErrorEvents({ ids: [...selectedIds] });
      toast.success(`${result.updated} Events als gelöst markiert`);
      setSelectedIds(new Set());
      await loadEvents();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Als gelöst markieren der Auswahl fehlgeschlagen'));
    } finally {
      setBulkPending(false);
    }
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);
      if (page <= 3) end = 4;
      else if (page >= totalPages - 2) start = totalPages - 3;
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  const activeChips = getActiveFilterChips(appliedFilter);
  const bulkDisabled = total === 0 || loading || bulkPending;

  const selectedCount = selectedIds.size;
  const allOnPageSelected = events.length > 0 && events.every((event) => selectedIds.has(event.id));
  const someOnPageSelected = events.some((event) => selectedIds.has(event.id));

  return (
    <div className="space-y-6">
      {/* Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="filter-service">Service</Label>
              <Select value={draftService} onValueChange={setDraftService}>
                <SelectTrigger id="filter-service">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {SERVICE_OPTIONS.map((service) => (
                    <SelectItem key={service} value={service}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="filter-error-identifier">Fehler-Identifier</Label>
              <Input
                id="filter-error-identifier"
                placeholder="z.B. session_start_failed"
                value={draftErrorIdentifier}
                onChange={(e) => setDraftErrorIdentifier(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="filter-customer-id">Kunden-ID</Label>
              <Input
                id="filter-customer-id"
                value={draftCustomerId}
                onChange={(e) => setDraftCustomerId(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="filter-severity">Severity</Label>
              <Select value={draftSeverity} onValueChange={setDraftSeverity}>
                <SelectTrigger id="filter-severity">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {SEVERITY_SELECT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="filter-status">Status</Label>
              <Select value={draftStatus} onValueChange={setDraftStatus}>
                <SelectTrigger id="filter-status">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {STATUS_SELECT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="filter-from">Von</Label>
              <Input
                id="filter-from"
                type="datetime-local"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="filter-to">Bis</Label>
              <Input
                id="filter-to"
                type="datetime-local"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                <RefreshCw className="mr-2 h-4 w-4" />
                Anwenden
              </Button>
              <Button variant="outline" onClick={handleResetFilters}>
                Zurücksetzen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>Error Events</CardTitle>
              <CardDescription>{loading ? 'Laden...' : `${total} Events insgesamt`}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={!appliedFilter.from || !appliedFilter.to || total === 0 || loading || exporting}
                title={
                  appliedFilter.from && appliedFilter.to
                    ? 'Alle Events des aktuellen Filters als CSV herunterladen'
                    : 'Für den Export Von und Bis setzen und anwenden'
                }
              >
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                CSV exportieren
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={bulkDisabled}>
                    <CircleCheckBig className="mr-2 h-4 w-4" />
                    Alle quittieren
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{total} Events quittieren?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Wirkt auf ALLE Events des aktuellen Filters, nicht nur die angezeigte Seite.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  {activeChips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 rounded border bg-gray-50 p-2">
                      {activeChips.map((chip) => (
                        <Badge key={chip.key} variant="outline">
                          {chip.label}: {chip.value}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction onClick={handleBulkAcknowledge}>Quittieren</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={bulkDisabled}>
                    <CheckCheck className="mr-2 h-4 w-4" />
                    Alle als gelöst markieren
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{total} Events als gelöst markieren?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Wirkt auf ALLE Events des aktuellen Filters, nicht nur die angezeigte Seite.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  {activeChips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 rounded border bg-gray-50 p-2">
                      {activeChips.map((chip) => (
                        <Badge key={chip.key} variant="outline">
                          {chip.label}: {chip.value}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-gray-500">
                    Noch offene Events erhalten dabei Quittiert-Zeitstempel und Bearbeiter; bereits gelöste bleiben
                    unverändert.
                  </p>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction onClick={handleBulkResolve}>Als gelöst markieren</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center justify-center p-8 text-red-600">
              <AlertCircle className="mr-2 h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadEvents} className="ml-4">
                Erneut versuchen
              </Button>
            </div>
          )}

          {loading && !error && (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="mr-2 h-6 w-6 animate-spin" />
              <span>Events werden geladen...</span>
            </div>
          )}

          {!loading && !error && (
            <>
              {selectedCount > 0 && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border bg-gray-50 px-3 py-2">
                  <span className="text-sm font-medium text-gray-700">{selectedCount} Events ausgewählt</span>
                  <div className="flex flex-wrap gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" disabled={bulkPending}>
                          <CircleCheckBig className="mr-2 h-4 w-4" />
                          Auswahl quittieren
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{selectedCount} ausgewählte Events quittieren?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Wirkt nur auf die per Checkbox ausgewählten Events — auch auf Auswahlen anderer Seiten.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction onClick={handleSelectionAcknowledge}>Quittieren</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" disabled={bulkPending}>
                          <CheckCheck className="mr-2 h-4 w-4" />
                          Auswahl als gelöst markieren
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{selectedCount} ausgewählte Events als gelöst markieren?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Wirkt nur auf die per Checkbox ausgewählten Events — auch auf Auswahlen anderer Seiten.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <p className="text-sm text-gray-500">
                          Noch offene Events erhalten dabei Quittiert-Zeitstempel und Bearbeiter; bereits gelöste
                          bleiben unverändert.
                        </p>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction onClick={handleSelectionResolve}>Als gelöst markieren</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                      Auswahl aufheben
                    </Button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="w-10 px-4 py-3">
                        <Checkbox
                          checked={allOnPageSelected ? true : someOnPageSelected ? 'indeterminate' : false}
                          onCheckedChange={(checked) => togglePageSelection(checked === true)}
                          disabled={events.length === 0}
                          aria-label="Alle Events dieser Seite auswählen"
                        />
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Zeitstempel</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Service</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Fehler</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Severity</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Kunde / Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-gray-500">
                          Keine Events gefunden
                        </td>
                      </tr>
                    )}
                    {events.map((event) => (
                      <tr
                        key={event.id}
                        className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                        onClick={() => openDetail(event)}
                      >
                        {/* stopPropagation: Klick auf die Checkbox darf nicht das Detail-Modal der Zeile öffnen. */}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(event.id)}
                            onCheckedChange={(checked) => toggleEventSelection(event.id, checked === true)}
                            aria-label="Event auswählen"
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {formatDateTime(event.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{event.service}</td>
                        <td className="px-4 py-3 font-mono text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            <span>{event.errorIdentifier}</span>
                            {/* stopPropagation: Klick auf Kopieren darf nicht das Detail-Modal der Zeile öffnen. */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void copyToClipboard(event.errorIdentifier, 'Fehler-Identifier');
                              }}
                              title="Fehler-Identifier kopieren"
                              className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <SeverityBadge severity={event.severity} />
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={event.status} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center gap-1.5 text-sm text-gray-700">
                            <User className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-label="Kunde" />
                            {event.customerId ? (
                              <span className="font-mono" title={event.customerId}>
                                {shortenUuid(event.customerId)}
                              </span>
                            ) : (
                              <span className="italic text-gray-400">nicht zuordenbar</span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                            <Bot className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-label="Agent" />
                            {event.agentId ? (
                              <span className="font-mono" title={event.agentId}>
                                {shortenUuid(event.agentId)}
                              </span>
                            ) : (
                              <span>–</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > 0 && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Einträge pro Seite:</span>
                      <Select
                        value={limit.toString()}
                        onValueChange={(val) => {
                          setLimit(parseInt(val, 10));
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGE_SIZE_OPTIONS.map((size) => (
                            <SelectItem key={size} value={size.toString()}>
                              {size}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-sm text-gray-600">
                      {(page - 1) * limit + 1}–{Math.min(page * limit, total)} von {total}
                    </span>
                  </div>

                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {getPageNumbers().map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      ) : (
                        <Button
                          key={p}
                          variant={page === p ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPage(p as number)}
                          className="w-8"
                        >
                          {p}
                        </Button>
                      ),
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ErrorEventDetailDialog
        event={selectedEvent}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onAcknowledge={handleAcknowledge}
        onResolve={handleResolve}
        actionPending={actionPending}
      />
    </div>
  );
}
